import { shortDate } from '../../../lib/formatLabels';
import { scale, smoothPath, segments } from '../../../components/charts/svgPath';

interface Props {
  /** Week start dates (ISO), oldest first. */
  weeks: string[];
  /** The student's weekly average per week; null where there is no attempt. */
  student: (number | null)[];
  /** The class (section) weekly average per week; omitted when the cohort is unknown. */
  classAvg?: (number | null)[] | null;
  /** Pass mark, drawn as a dashed line. */
  passMark?: number;
  ariaLabel: string;
}

const LO = 20;
const HI = 80;
const clamp = (v: number) => Math.max(LO, Math.min(HI, v));

/**
 * Artboard weekly-average chart (760x220): the student's line in accent over a faint area, the
 * class average dashed in slate, a dashed pass-mark line, y from 20 to 80 and an end dot.
 */
export default function StudentTrendChart({ weeks, student, classAvg = null, passMark = 50, ariaLabel }: Props) {
  const n = weeks.length;
  const x = scale(0, Math.max(1, n - 1), 50, 740);
  const y = scale(LO, HI, 190, 10);
  const path = (vals: (number | null)[]) => segments(vals).filter((r) => r.length > 1)
    .map((run) => smoothPath(run.map((p) => [x(p.i), y(clamp(p.v))] as [number, number])));
  const studentRuns = path(student);
  const classRuns = classAvg ? path(classAvg) : [];
  const lastIdx = (() => { for (let i = student.length - 1; i >= 0; i -= 1) if (student[i] != null) return i; return -1; })();
  const every = Math.max(1, Math.ceil(n / 6));
  return (
    <svg viewBox="0 0 760 220" className="block h-auto w-full" role="img" aria-label={ariaLabel}>
      {[20, 40, 60, 80].map((t) => (
        <g key={t}>
          <line x1={40} x2={750} y1={y(t)} y2={y(t)} className="stroke-line" />
          <text x={30} y={y(t) + 4} textAnchor="end" className="fill-muted font-mono text-[11px]">{t}</text>
        </g>
      ))}
      <line x1={40} x2={750} y1={y(passMark)} y2={y(passMark)} className="stroke-accent-ink" strokeWidth={1.5} strokeDasharray="5 5" />
      {classRuns.map((d, k) => <path key={`c${k}`} d={d} fill="none" className="stroke-slate" strokeWidth={2} strokeDasharray="6 5" />)}
      {segments(student).filter((r) => r.length > 1).map((run, k) => (
        <path key={`a${k}`} fillOpacity={0.12} className="fill-accent"
          d={`${smoothPath(run.map((p) => [x(p.i), y(clamp(p.v))] as [number, number]))} L${x(run[run.length - 1].i)},190 L${x(run[0].i)},190 Z`} />
      ))}
      {studentRuns.map((d, k) => <path key={`s${k}`} d={d} fill="none" className="stroke-accent" strokeWidth={3} strokeLinejoin="round" strokeLinecap="round" />)}
      {lastIdx >= 0 && <circle cx={x(lastIdx)} cy={y(clamp(student[lastIdx] as number))} r={6} className="fill-surface stroke-ink" strokeWidth={3} />}
      {weeks.map((w, i) => (i % every === 0 || i === n - 1) && (
        <text key={w} x={x(i)} y={212} textAnchor="middle" className="fill-muted font-mono text-[11px]">{shortDate(w)}</text>
      ))}
    </svg>
  );
}
